import cx from 'classnames';
import type { ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { FloorPlanBackgroundImg } from '@components/floorPlan/FloorPlanBackgroundImg';
import { ZoomControls } from '@components/floorPlan/ZoomControls';
import { useLoadImage } from '@shared/hooks/useLoadImage';
import typography from '~styles/typography.scss';
import styles from './FloorPlan.scss';

const DEFAULT_INITIAL_TABLE_ICON_SCALE = 1;
const DEFAULT_CONTAINER_WIDTH = 960;
const REFRESH_RATE = 0;
const FLOOR_PLAN_INTRINSIC_WIDTH = 1050;
const FLOOR_PLAN_INTRINSIC_HEIGHT = 745;
const MINIMUM_SCALE_SCALE_TO_FIT = 0.1;
// Extra bottom space on the TransformComponent wrapper so its
// overflow: hidden doesn't clip the floor plan's bottom border stroke.
const WRAPPER_BOTTOM_BUFFER = 6;

export const MINIMUM_SCALE = 1;
export const MAXIMUM_SCALE = 6;

export type FloorPlanTablesRenderer = (tableIconScale: number) => ReactNode;

export interface FloorPlanProps {
  backgroundSrc?: string;
  className?: string;
  floorPlanTablesRenderer: FloorPlanTablesRenderer;
  handleBackgroundOnClick?: () => void;
  disableControls?: boolean;
  showZoomControls?: boolean;
  testId?: string;
  scaleOnVerticalResize?: boolean;
}

export const FloorPlan = forwardRef<ReactZoomPanPinchRef, FloorPlanProps>(
  (
    {
      backgroundSrc,
      className,
      floorPlanTablesRenderer,
      handleBackgroundOnClick,
      disableControls = false,
      showZoomControls = true,
      testId = 'floor-plan',
      scaleOnVerticalResize = false,
    },
    zoomRef,
  ) => {
    const [mouseDownTime, setMouseDownTime] = useState<number>(Infinity);
    const { initialWidth: initialImageSrcWidth, isLoaded } = useLoadImage(
      backgroundSrc || '',
    );
    const {
      ref,
      width: detectedWidth,
      height: detectedHeight,
    } = useResizeDetector({
      refreshMode: 'debounce',
      refreshRate: REFRESH_RATE,
    });
    const [containerWidth, setContainerWidth] = useState(
      detectedWidth ?? DEFAULT_CONTAINER_WIDTH,
    );
    const [containerHeight, setContainerHeight] = useState(detectedHeight ?? 0);
    const [tableIconScale, setTableIconScale] = useState(
      DEFAULT_INITIAL_TABLE_ICON_SCALE,
    );

    const scaleToFitWidth = containerWidth / FLOOR_PLAN_INTRINSIC_WIDTH;
    const scaleToFitHeight =
      containerHeight > 0
        ? containerHeight / FLOOR_PLAN_INTRINSIC_HEIGHT
        : scaleToFitWidth;
    const fitScale = Math.min(scaleToFitWidth, scaleToFitHeight);
    const displayWidth = FLOOR_PLAN_INTRINSIC_WIDTH * fitScale;
    const computedScale = Math.min(fitScale, MAXIMUM_SCALE);

    useEffect(() => {
      if (!scaleOnVerticalResize) return;
      if (!zoomRef || typeof zoomRef === 'function') return;
      if (!zoomRef.current) return;
      if (typeof zoomRef.current.centerView !== 'function') return;

      zoomRef.current.centerView(computedScale);
    }, [scaleOnVerticalResize, computedScale, zoomRef]);

    useEffect(() => {
      setContainerWidth(detectedWidth ?? DEFAULT_CONTAINER_WIDTH);
      setContainerHeight(detectedHeight ?? 0);
    }, [detectedWidth, detectedHeight]);

    useEffect(() => {
      if (!scaleOnVerticalResize) return;
      let timeoutId: ReturnType<typeof setTimeout>;
      const onResize = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          requestAnimationFrame(() => {
            if (ref.current) {
              const rect = ref.current.getBoundingClientRect();
              setContainerWidth(rect.width);
              setContainerHeight(rect.height);
            }
          });
        }, 100);
      };
      window.addEventListener('resize', onResize);
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', onResize);
      };
    }, [scaleOnVerticalResize, ref]);

    const noFloorPlanAvailable = !backgroundSrc;

    const effectiveContainerWidth = scaleOnVerticalResize
      ? displayWidth
      : (detectedWidth ?? DEFAULT_CONTAINER_WIDTH);

    // Safari does not reliably auto-size a flex container whose height
    // depends on transformed descendants (react-zoom-pan-pinch uses CSS
    // transforms for panning/zoom), so we give it an explicit height
    // instead of relying on layout-computed auto height.
    const explicitContainerHeight =
      effectiveContainerWidth *
      (FLOOR_PLAN_INTRINSIC_HEIGHT / FLOOR_PLAN_INTRINSIC_WIDTH);

    const floorPlanContainerClassNames = cx({
      [className || '']: className,
      [styles.floorPlanContainer]: true,
      [styles.floorPlanContainerScaleToFit]: scaleOnVerticalResize,
      [styles.floorPlanLoaded]: isLoaded,
    });

    if (noFloorPlanAvailable) {
      return (
        <div className={styles.noFloorPlanContainer} data-testid="floor-plan">
          <p className={typography.d4}>No floor plan available.</p>
        </div>
      );
    }

    const DELAY = 200;
    const handleOnMouseDown = () => {
      setMouseDownTime(Date.now());
    };

    const handleOnMouseUp = () => {
      if (Date.now() - mouseDownTime < DELAY) {
        handleBackgroundOnClick?.();
        setMouseDownTime(Infinity);
      }
    };

    const renderFloorPlanTables = () => {
      if (!Number.isFinite(tableIconScale)) return null;

      return floorPlanTablesRenderer(tableIconScale);
    };

    return (
      <div
        className={floorPlanContainerClassNames}
        data-testid={testId}
        ref={ref}
        style={
          scaleOnVerticalResize
            ? { maxHeight: FLOOR_PLAN_INTRINSIC_HEIGHT }
            : undefined
        }
      >
        <TransformWrapper
          {...(scaleOnVerticalResize && { centerOnInit: true })}
          wheel={{
            smoothStep: 0.01,
            wheelDisabled: true,
          }}
          panning={{
            velocityDisabled: true,
          }}
          zoomAnimation={{
            size: 0.25,
          }}
          disabled={disableControls}
          ref={zoomRef}
          minScale={scaleOnVerticalResize ? computedScale : MINIMUM_SCALE}
          maxScale={MAXIMUM_SCALE}
          {...(scaleOnVerticalResize && {
            initialScale: computedScale,
          })}
        >
          {!disableControls && showZoomControls && (
            <ZoomControls
              minScale={
                scaleOnVerticalResize
                  ? MINIMUM_SCALE_SCALE_TO_FIT
                  : MINIMUM_SCALE
              }
              maxScale={MAXIMUM_SCALE}
              scale={
                scaleOnVerticalResize
                  ? MINIMUM_SCALE_SCALE_TO_FIT
                  : MINIMUM_SCALE
              }
            />
          )}
          <TransformComponent
            wrapperStyle={{
              height: explicitContainerHeight + WRAPPER_BOTTOM_BUFFER,
            }}
            contentStyle={{ height: explicitContainerHeight }}
          >
            <div
              aria-label="floor plan background"
              className={styles.floorPlanBackground}
              onMouseDown={handleOnMouseDown}
              onMouseUp={handleOnMouseUp}
            >
              <FloorPlanBackgroundImg
                backgroundSrc={backgroundSrc}
                containerWidth={effectiveContainerWidth}
                initialImageSrcWidth={initialImageSrcWidth}
                setTableIconScale={setTableIconScale}
              />
            </div>
            {renderFloorPlanTables()}
          </TransformComponent>
        </TransformWrapper>
      </div>
    );
  },
);

FloorPlan.displayName = 'FloorPlan';
